// src/App.js
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext.js";
import { LanguageProvider } from "./context/LanguageContext.js";
import { AirbnbAuthProvider } from "./context/AirbnbAuthContext.js";
import { AgendaAuthProvider } from "./context/AgendaAuthContext.js";
import ProtectedRoute   from "./components/ProtectedRoute.js";
import DashboardLayout  from "./components/DashboardLayout.js";

import Login                from "./pages/Login.js";
import HomePage             from "./pages/HomePage.js";
import ColaboradorPage      from "./pages/ColaboradorPage.js";
import ReportsPage          from "./pages/ReportsPage.js";
import AdminUsers           from "./pages/AdminUsers.js";         // lista de usuários
import AdminUserProfile     from "./pages/AdminUserProfile.js";   // perfil completo do usuário
import ClientsPage          from "./pages/ClientsPage.js";
import HealthSafetyPage     from "./pages/HealthSafetyPage.js";
import DocumentViewerPage   from "./pages/DocumentViewerPage.js";
import TrainingTestPage     from "./pages/TrainingTestPage.js";
import TrackingPage         from "./pages/TrackingPage.js";         // rastreamento GPS admin
import TrackingPersonPage   from "./pages/TrackingPersonPage.js"; // rastreamento GPS por pessoa
import AdminFixData         from "./pages/AdminFixData.js";       // ferramentas de manutenção
import GalleryPage          from "./pages/GalleryPage.js";       // galeria de arquivos
import PublicGalleryPage    from "./pages/PublicGalleryPage.js"; // galeria pública de documentos
import DocumentsPage        from "./pages/DocumentsPage.js";     // documentos de serviços
import CalendarPage         from "./pages/CalendarPage.js";      // calendário de jobs
import SyncXeroPage         from "./pages/SyncXeroPage.js";      // sincronização com Xero
import InvoiceHistoryPage   from "./pages/InvoiceHistoryPage.js"; // histórico de invoices
import RevenuePage from "./pages/RevenuePage.js"; // relatório de faturamento
import ProfitAnalysisPage from "./pages/ProfitAnalysisPage.js"; // análise de lucro
import AIAssistantPage from "./pages/AIAssistantPage.js"; // AI assistant for client quotes
import AIAssistantManagementPage from "./pages/AIAssistantManagementPage.js"; // AI assistants management
import ConversationViewerPage from "./pages/ConversationViewerPage.js"; // WhatsApp conversations for AI training
import TrainingSessionsPage from "./pages/TrainingSessionsPage.js"; // AI training sessions
import AirbnbRegisterPage from "./pages/AirbnbRegisterPage.js"; // Airbnb client registration
import AirbnbLoginPage from "./pages/AirbnbLoginPage.js"; // Airbnb client login
import AirbnbPortalPage from "./pages/AirbnbPortalPage.js"; // Airbnb booking portal
import AirbnbManagementPage from "./pages/AirbnbManagementPage.js"; // Airbnb management for admins
import WebsiteSubmissionsPage from "./pages/WebsiteSubmissionsPage.js"; // Website submissions management
import FeedbackManagerPage from "./pages/FeedbackManagerPage.js"; // Feedback manager for WhatsApp
import EmailMarketingPage from "./pages/EmailMarketingPage.js"; // Email marketing for clients
import PersonalAgendaPage from "./pages/PersonalAgendaPage.js"; // Admin personal agenda
import AgendaLoginPage from "./pages/AgendaLoginPage.js"; // Agenda multi-user login
import AgendaInvitePage from "./pages/AgendaInvitePage.js"; // Agenda invite registration
import AgendaCalendarPage from "./pages/AgendaCalendarPage.js"; // Agenda user calendar
import AgendaUsersPage from "./pages/AgendaUsersPage.js"; // Agenda admin management
import TutorialPage from "./pages/TutorialPage.js"; // Tutorial para colaboradores
import QuotesManagerPage from "./pages/QuotesManagerPage.js"; // Gerenciador de orçamentos Xero
import PayrollPage from "./pages/PayrollPage.js"; // Payroll system

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AirbnbAuthProvider>
          <AgendaAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Rotas públicas (sem autenticação) */}
              <Route path="/login" element={<Login />} />
              <Route path="/documents/gallery/:id" element={<PublicGalleryPage />} />

              {/* Rotas Airbnb (sem layout de dashboard) */}
              <Route path="/airbnb/register" element={<AirbnbRegisterPage />} />
              <Route path="/airbnb/login" element={<AirbnbLoginPage />} />
              <Route path="/airbnb/portal" element={<AirbnbPortalPage />} />

              {/* Rotas Agenda Multi-User (sem layout de dashboard) */}
              <Route path="/agenda" element={<AgendaCalendarPage />} />
              <Route path="/agenda/login" element={<AgendaLoginPage />} />
              <Route path="/agenda/invite/:token" element={<AgendaInvitePage />} />

            {/* Rotas autenticadas */}
            <Route
              path="/*"
              element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    {/* Home */}
                    <Route index element={<HomePage />} />

                    {/* Colaborador (normal ou admin via paramUid) */}
                    <Route
                      path="colaborador/:slug"
                      element={<ColaboradorPage />}
                    />

                    <Route path="tutorial" element={<TutorialPage />} />

                    <Route
                      path="admin/clients"
                      element={
                        <ProtectedRoute adminOnly clientManagerAllowed>
                          <ClientsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="admin/airbnb-management"
                      element={
                        <ProtectedRoute adminOnly>
                          <AirbnbManagementPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin/Manager: Website Submissions (Its So Clean) */}
                    <Route
                      path="admin/website-submissions"
                      element={
                        <ProtectedRoute adminOnly clientManagerAllowed>
                          <WebsiteSubmissionsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Feedback Manager (WhatsApp) */}
                    <Route
                      path="admin/feedback-manager"
                      element={
                        <ProtectedRoute adminOnly>
                          <FeedbackManagerPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Quotes Manager */}
                    <Route
                      path="admin/quotes-manager"
                      element={
                        <ProtectedRoute adminOnly>
                          <QuotesManagerPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Payroll */}
                    <Route
                      path="admin/payroll"
                      element={
                        <ProtectedRoute adminOnly>
                          <PayrollPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Email Marketing */}
                    <Route
                      path="admin/email-marketing"
                      element={
                        <ProtectedRoute adminOnly>
                          <EmailMarketingPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Personal Agenda */}
                    <Route
                      path="admin/personal-agenda"
                      element={
                        <ProtectedRoute adminOnly>
                          <PersonalAgendaPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: Agenda Users Management */}
                    <Route
                      path="admin/agenda-users"
                      element={
                        <ProtectedRoute adminOnly>
                          <AgendaUsersPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Gallery */}
                    <Route path="gallery" element={<GalleryPage />} />

                    {/* Health & Safety Routes */}
                    <Route path="health-safety" element={<HealthSafetyPage />} />
                    <Route path="health-safety/document/:documentId" element={<DocumentViewerPage />} />
                    <Route path="health-safety/training" element={<TrainingTestPage />} />

                    <Route
                      path="admin/users/:uid/colaborador/:slug"
                      element={
                        <ProtectedRoute adminOnly>
                          <ColaboradorPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reports (normal ou admin via paramUid) */}
                    <Route
                      path="reports/:slug"
                      element={<ReportsPage />}
                    />
                    <Route
                      path="admin/users/:uid/reports/:slug"
                      element={
                        <ProtectedRoute adminOnly>
                          <ReportsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: documentos de serviços */}
                    <Route
                      path="admin/documents"
                      element={
                        <ProtectedRoute adminOnly>
                          <DocumentsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: lista de usuários */}
                    <Route
                      path="admin/users"
                      element={
                        <ProtectedRoute adminOnly>
                          <AdminUsers />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: perfil completo do usuário */}
                    <Route
                      path="admin/users/:uid/profile"
                      element={
                        <ProtectedRoute adminOnly>
                          <AdminUserProfile />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: rastreamento GPS */}
                    <Route
                      path="admin/tracking"
                      element={
                        <ProtectedRoute adminOnly>
                          <TrackingPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: rastreamento GPS por pessoa */}
                    <Route
                      path="admin/tracking/:displayName"
                      element={
                        <ProtectedRoute adminOnly>
                          <TrackingPersonPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: ferramentas de manutenção */}
                    <Route
                      path="admin/fix-data"
                      element={
                        <ProtectedRoute adminOnly>
                          <AdminFixData />
                        </ProtectedRoute>
                      }
                    />

                    {/* Calendário de jobs - acessível para todos os usuários autenticados */}
                    <Route
                      path="admin/calendar"
                      element={<CalendarPage />}
                    />

                    {/* Admin: sincronização com Xero */}
                    <Route
                      path="admin/sync-xero"
                      element={
                        <ProtectedRoute adminOnly>
                          <SyncXeroPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: histórico de invoices */}
                    <Route
                      path="admin/invoice-history"
                      element={
                        <ProtectedRoute adminOnly>
                          <InvoiceHistoryPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: relatório de faturamento */}
                    <Route
                      path="admin/revenue"
                      element={
                        <ProtectedRoute adminOnly>
                          <RevenuePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: análise de lucro */}
                    <Route
                      path="admin/profit-analysis"
                      element={
                        <ProtectedRoute adminOnly>
                          <ProfitAnalysisPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin/Manager: AI Assistant para cotações */}
                    <Route
                      path="admin/ai-assistant"
                      element={
                        <ProtectedRoute adminOnly clientManagerAllowed>
                          <AIAssistantPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: AI Assistants Management */}
                    <Route
                      path="admin/ai-assistant-management"
                      element={
                        <ProtectedRoute adminOnly clientManagerAllowed>
                          <AIAssistantManagementPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: WhatsApp Conversations for AI Training */}
                    <Route
                      path="admin/training/conversations"
                      element={
                        <ProtectedRoute adminOnly>
                          <ConversationViewerPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin: AI Training Sessions */}
                    <Route
                      path="admin/training/sessions"
                      element={
                        <ProtectedRoute adminOnly>
                          <TrainingSessionsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Qualquer outra subrota → home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

            {/* Se nada bater, vai para login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
          </AgendaAuthProvider>
        </AirbnbAuthProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
